import { PostContentBlock, postContentBlocks } from 'app/blocks'
import { ContentCardProps } from 'app/components/content/ContentCard'
import { commentFragments } from 'app/queries/comments/comments'
import {
  contentFragments,
  mapToContentCard,
} from 'app/queries/content/all-content-list'
import { gql } from 'graphql-request'

export interface Content {
  id: string
  slug: string
  href: string
  title: string
  createdAt: string
  commentCount: number
  image: string
  contentTier: number | 'free'
  contentTierSlug: string
  primaryCollectionId: string
  disableRecommendations: boolean
  content: PostContentBlock[]
  category: string
  categoryHref: string
  videoPost: boolean
  shareTitle: string
  shareDescription: string
  tags: {
    label: string
    href: string
  }[]
  totalFavourites: number
  currentRating: number
  isFavourited: boolean
  related: ContentCardProps[]
  previous: {
    slug: string
  }
  next: {
    slug: string
  }
  comments?: any
  isUnlocked: boolean
  canUnlock: boolean
  preventUnlocks: boolean
  preventPreviews: boolean
  disableComments?: boolean
  isInSeries: boolean
  currentProgress?: number
   collections: {
    label: string
    href: string
  }[]
}

export const contentFragment = gql`
  ${postContentBlocks}
  fragment contentFragment on Content {
    id
    slug
    title
    publishedAt
    createdAt
    commentCount
    videoPost
    shareTitle
    shareDescription
    disableComments
    contentTier {
      planLevel
      preventUnlocks
      preventPreviews
      slug
    }

    thumbnail {
      id
      url
      sizes {
        thumbnail {
          url
        }
      }
    }

    primaryCollection {
      id
      name
      slug
      disableRecommendations
      preventUnlocks
      preventPreviews
      isSeries
    }

    collections {
      name
      slug
      preventUnlocks
      preventPreviews
      isSeries
    }

    preventUnlocks
    preventPreviews

    tags {
      name
      slug
    }

    content {
      __typename
      ...richTextFragment
      ...videoFragment
      ...imageFragment
      ...externalImageFragment
      ...spotifyEmbedFragment
      ...promoCodeFragment
      ...pollFragment
      ...imageCarouselFragment
      ...productGridFragment
      ...formFragment
    }
  }
`

export const createContentPreviewQuery = () => gql`
  ${contentFragment}
  query getContentPreview($id: String!) {
    Content(id: $id, draft: true) {
      ...contentFragment
    }
  }
`

export const createContentQuery = () => gql`
  ${contentFragment}
  ${contentFragments}
  query getContent(
    $slug: String!
    $collection: String
    $contentTier: String
    $tag: String
  ) {
    fetchSingleContent(slug: $slug) {
      content {
        ...contentFragment
      }
    }

    # the total number of favourites for the post
    totalFavourites(slug: $slug)

    # the users current rating of the post
    currentRating(slug: $slug)

    # whether the user has favorited the post
    hasFavourited(slug: $slug)

    # whether the user has unlocked the post
    hasUnlocked(slug: $slug)

    # how much of the video the user has watched
    currentProgress(slug: $slug)

    # whether the user can unlock posts
    canUnlock

    relatedContent(
      slug: $slug
      collection: $collection
      contentTier: $contentTier
      tag: $tag
    ) {
      docs {
        ...content
      }
      previous {
        slug
      }
      next {
        slug
      }
      defaultThumbnail {
        sizes {
          thumbnail {
            url
          }
        }
      }
    }
  }
`

export const createContentSsrQuery = () => gql`
  ${contentFragment}
  ${commentFragments}
  query getContent($slug: String!) {
    fetchSingleContent(slug: $slug) {
      content {
        ...contentFragment
      }
    }

    # the total number of favourites for the post
    totalFavourites(slug: $slug)

    # the users current rating of the post
    currentRating(slug: $slug)

    # whether the user has favorited the post
    hasFavourited(slug: $slug)

    # whether the user has unlocked the post
    hasUnlocked(slug: $slug)

    # how much of the video the user has watched
    currentProgress(slug: $slug)

    # whether the user can unlock posts
    canUnlock

    commentsBySlug(slug: $slug, limit: 10) {
      totalDocs
      page
      docs {
        ...commentFragment
      }
    }
  }
`

export const mapToContent = (doc: any): Content | null => {
  const content = doc?.fetchSingleContent?.content
  if (!content) return null

  return mapSingle(content, doc)
}

export const mapToPreviewContent = (doc: any): Content | null => {
  const content = doc?.Content || null
  if (!content) return null

  return mapSingle(content, doc)
}

export const mapSingle = (content: any, data: any): Content => {
  const collections = (content.collections || []).map((collection) => ({
    label: collection.name,
    href: `/collection/${collection.slug}`,
  }))

  const tags = (content.tags || []).map((tag) => ({
    label: tag.name,
    href: `/tag/${tag.slug}`,
  }))

  const concat = [...collections, ...tags]

  const collectionsPreventUnlocks = content?.collections?.some(
    (collection: any) => collection.preventUnlocks
  )

  const collectionsPreventPreviews = content?.collections?.some(
    (collection: any) => collection.preventPreviews
  )

  const preventUnlocks =
    content.preventUnlocks ||
    collectionsPreventUnlocks ||
    content.contentTier?.preventUnlocks

  const preventPreviews =
    content.preventPreviews ||
    collectionsPreventPreviews ||
    content.contentTier?.preventPreviews

  return {
    id: content.id || '',
    slug: content.slug || '',
    href: `/content/${content.slug}`,
    title: content.title || '',
    contentTier: content.contentTier?.planLevel || 'initiate',
    contentTierSlug: content.contentTier?.slug || '',
    primaryCollectionId: content.primaryCollection?.id || '',
    disableRecommendations:
      content.primaryCollection?.disableRecommendations || false,
    content: (content.content as PostContentBlock[]) || [],
    createdAt: content.publishedAt || content.createdAt || '',
    commentCount: content.commentCount || 0,
    image:
      content.thumbnail?.sizes?.thumbnail?.url ||
      content.thumbnail?.url ||
      null,
    category: content.primaryCollection?.name || null,
    categoryHref: content.primaryCollection?.slug || null,
    tags: concat || [],
    videoPost: content.videoPost || false,
    shareTitle: content.shareTitle,
    shareDescription: content.shareDescription,
    totalFavourites: data.totalFavourites || 0,
    currentRating: data.currentRating || 0,
    isFavourited: data.hasFavourited || false,
    related:
      data.relatedContent?.docs.map((content: any) =>
        mapToContentCard(content, {
          Defaults: {
            thumbnail: data.relatedContent?.defaultThumbnail,
          },
        })
      ) || [],
    previous: data.relatedContent?.previous || null,
    next: data.relatedContent?.next || null,
    comments: data?.commentsBySlug || [],
    isUnlocked: !!data.hasUnlocked,
    canUnlock: !!data.canUnlock,
    preventUnlocks: !!preventUnlocks,
    preventPreviews: !!preventPreviews,
    disableComments: content?.disableComments || false,
    isInSeries: content?.primaryCollection?.isSeries || false,
    currentProgress: data.currentProgress || 0,
    collections,
  }
}
